import { IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IAccountSubscription, IUpgradePlan, IUpgradedEdition } from "../../configuration/models";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { SubscriptionService } from "@gtmhub/subscriptions/subscriptionService";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { IAccount } from "@gtmhub/core";
import { IPlan, BillingPeriod } from "../models";

export interface IUpgradeEditionPlanResolveParams {
  selectedPlan: IPlan;
}

interface IUpgradeEditionPlanScope extends IScope {
  indicators: {
    loading: boolean;
    saving: boolean;
    successfulUpdate: boolean;
  };
  selectedPlan: IPlan;
  billing: {
    period: BillingPeriod;
    regularUsersCount: number;
    restrictedUsersCount: number;
    price: number;
    totalBillingAmount: number;
    restrictedUsersPrice: number;
  };
  restrictedAddOnEnabled: boolean;
  additionalAccountData: {
    accountSubscriptionType: number;
    nextBillingAt: string;
    trialExpirationDays: string;
  };
  upgradeEditionPlan(): void;
  contactSales(): void;
}

export class UpgradeEditionPlanCtrl {
  private account: IAccount;

  public static $inject = [
    "$scope",
    "$state",
    "AccountService",
    "AccountResolverService",
    "UIErrorHandlingService",
    "SubscriptionService",
    "AnalyticsService",
    "resolveParams",
  ];

  constructor(
    private $scope: IUpgradeEditionPlanScope,
    private $state: StateService,
    private accountService: AccountService,
    private accountResolverService: AccountResolverService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private subscriptionService: SubscriptionService,
    private analyticsService: AnalyticsService,
    private resolveParams: IUpgradeEditionPlanResolveParams
  ) {
    this.init();
  }

  private init = (): void => {
    this.$scope.indicators = {
      loading: true,
      successfulUpdate: false,
      saving: false,
    };

    this.$scope.billing = {
      period: "annual",
      regularUsersCount: 0,
      restrictedUsersCount: 0,
      price: 0,
      totalBillingAmount: 0,
      restrictedUsersPrice: 1,
    };

    this.$scope.additionalAccountData = {
      accountSubscriptionType: null,
      nextBillingAt: null,
      trialExpirationDays: null,
    };

    this.account = this.accountResolverService.getAccountData();
    this.$scope.selectedPlan = this.resolveParams.selectedPlan;
    this.$scope.billing.period = this.$scope.selectedPlan.billingPeriod;
    this.$scope.upgradeEditionPlan = this.upgradeEditionPlan;
    this.$scope.contactSales = this.contactSales;

    this.$scope.additionalAccountData.accountSubscriptionType = this.account.type;
    if (this.$scope.additionalAccountData.accountSubscriptionType === 0) {
      this.$scope.additionalAccountData.trialExpirationDays = this.accountResolverService.checkExpirationOfTrialAccount().timeLeft;
      this.upgradeEditionPlan();
    } else {
      this.getAccountSubscription();
    }
  };

  private getAccountSubscription = (): void => {
    this.accountService.getAccountSubscription().then(
      (accountSubscription: IAccountSubscription) => {
        this.$scope.indicators.loading = false;

        if (accountSubscription.items.length) {
          accountSubscription.items.forEach((item) => {
            if (item.type === "regular") {
              this.$scope.billing.regularUsersCount = item.usersAllowed;
            }

            if (item.type === "restricted") {
              this.$scope.restrictedAddOnEnabled = true;
              this.$scope.billing.restrictedUsersCount = item.usersAllowed;
            }
          });
        }

        this.$scope.billing.price = this.$scope.selectedPlan.price;

        if (this.$scope.selectedPlan.billingPeriod === "annual") {
          this.$scope.billing.price = this.$scope.selectedPlan.price * 12;
        }

        if (this.$scope.restrictedAddOnEnabled) {
          if (this.$scope.selectedPlan.billingPeriod === "annual") {
            this.$scope.billing.restrictedUsersPrice = 12;
          }

          // total bill is the amount after users count from the subscription are multiplied by the billing price per user
          // plus all restricted users count is multiplied by the price of one dollar -
          // this is the current value for each restricted user of each edition plan
          this.$scope.billing.totalBillingAmount =
            this.$scope.billing.regularUsersCount * this.$scope.billing.price + this.$scope.billing.restrictedUsersCount * this.$scope.billing.restrictedUsersPrice;
        } else {
          this.$scope.billing.totalBillingAmount = this.$scope.billing.regularUsersCount * this.$scope.billing.price;
        }
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading" })
    );
  };

  private upgradeEditionPlan = (): void => {
    this.$scope.indicators.saving = true;

    const editionUpgrade: IUpgradePlan = {
      edition: this.$scope.selectedPlan.editionName,
      billingPeriod: this.$scope.selectedPlan.billingPeriod,
    };

    this.subscriptionService.upgradeEditionPlan(editionUpgrade).then(
      (upgradedData: IUpgradedEdition) => {
        this.$scope.indicators.saving = false;
        // this indicator will get into this function as true only if the account type is trial
        if (this.$scope.indicators.loading) {
          this.$scope.indicators.loading = false;
        }
        this.$scope.indicators.successfulUpdate = true;
        this.$scope.additionalAccountData.nextBillingAt = upgradedData.nextBillingAt;
        // replacing the new edition data once in the rootscope variable
        this.accountResolverService.setEdition(upgradedData.edition);
        // and once in the account key in localstorage where there is an edition property
        this.account.edition = upgradedData.edition;
        this.accountResolverService.setAccount(this.account);
        this.$state.go(".", {}, { reload: true });
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "saving", inline: true })
    );
  };

  private contactSales = (): void => {
    this.analyticsService.contactSales("contactSales");
  };
}
